/**
 * Сервис для управления хранением результатов анализов в localStorage
 * Сохраняет external_id анализов с ключом {user_id}_{analysis_number}
 */

import { getUserId } from './userIdService';

/**
 * Интерфейс для данных анализа в localStorage
 */
interface StoredAnalysisData {
  external_id: string;
  created_at: string; // ISO строка даты
  title?: string; // Опционально для удобства
  task_ids?: string[]; // Массив taskId для статистики LLM
}

/**
 * Генерирует ключ для хранения данных анализа
 */
function getAnalysisKey(userId: string, analysisNumber: number): string {
  return `analysis_${userId}_${analysisNumber}`;
}

/**
 * Сохраняет данные анализа в localStorage
 */
export function storeAnalysisData(analysisNumber: number, externalId: string, title?: string): void {
  try {
    const userId = getUserId();
    const key = getAnalysisKey(userId, analysisNumber);
    const data: StoredAnalysisData = {
      external_id: externalId,
      created_at: new Date().toISOString(),
      title: title
    };
    
    localStorage.setItem(key, JSON.stringify(data));
    console.log('💾 Анализ сохранен в localStorage:', { key, externalId, title });
  } catch (error) {
    console.error('Ошибка при сохранении данных анализа:', error);
  }
}

/**
 * Получает данные анализа из localStorage по номеру анализа
 */
export function getAnalysisData(analysisNumber: number): StoredAnalysisData | null {
  try {
    const userId = getUserId();
    const key = getAnalysisKey(userId, analysisNumber);
    const data = localStorage.getItem(key);
    
    if (!data) {
      return null;
    }
    
    return JSON.parse(data) as StoredAnalysisData;
  } catch (error) {
    console.error('Ошибка при получении данных анализа:', error);
    return null;
  }
}

/**
 * Получает данные анализа из localStorage по ключу {user_id}_{analysis_number}
 * Используется для загрузки анализа по URL
 */
export function getAnalysisDataByKey(userId: string, analysisNumber: number): StoredAnalysisData | null {
  try {
    const key = getAnalysisKey(userId, analysisNumber);
    const data = localStorage.getItem(key);
    
    if (!data) {
      return null;
    }
    
    return JSON.parse(data) as StoredAnalysisData;
  } catch (error) {
    console.error('Ошибка при получении данных анализа по ключу:', error);
    return null;
  }
}

/**
 * Удаляет данные анализа из localStorage
 */
export function removeAnalysisData(analysisNumber: number): void {
  try {
    const userId = getUserId();
    const key = getAnalysisKey(userId, analysisNumber);
    localStorage.removeItem(key);
    console.log('🗑️ Данные анализа удалены из localStorage:', key);
  } catch (error) {
    console.error('Ошибка при удалении данных анализа:', error);
  }
}

/**
 * Получает список всех сохраненных анализов текущего пользователя
 */
export function getAllStoredAnalyses(): Array<{ key: string; data: StoredAnalysisData; analysisNumber: number }> {
  try {
    const userId = getUserId();
    const prefix = `analysis_${userId}_`;
    const analyses: Array<{ key: string; data: StoredAnalysisData; analysisNumber: number }> = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        const analysisNumber = parseInt(key.replace(prefix, ''), 10);
        const data = localStorage.getItem(key);
        
        if (data && !isNaN(analysisNumber)) {
          try {
            const parsedData = JSON.parse(data) as StoredAnalysisData;
            analyses.push({ key, data: parsedData, analysisNumber });
          } catch (parseError) {
            console.warn('Ошибка парсинга данных анализа:', key, parseError);
          }
        }
      }
    }
    
    // Сортируем по номеру анализа (по убыванию)
    analyses.sort((a, b) => b.analysisNumber - a.analysisNumber);
    
    return analyses;
  } catch (error) {
    console.error('Ошибка при получении списка анализов:', error);
    return [];
  }
}

/**
 * Очищает все данные анализов текущего пользователя
 */
export function clearAllAnalysisData(): void {
  try {
    const userId = getUserId();
    const prefix = `analysis_${userId}_`;
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log('🧹 Очищены все данные анализов пользователя:', keysToRemove.length);
  } catch (error) {
    console.error('Ошибка при очистке данных анализов:', error);
  }
}

/**
 * Проверяет, является ли анализ устаревшим (старше 1 дня)
 */
export function isAnalysisExpired(createdAt: string): boolean {
  try {
    const createdDate = new Date(createdAt);
    const now = new Date();
    const daysDiff = (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysDiff > 1;
  } catch (error) {
    console.error('Ошибка при проверке срока действия анализа:', error);
    return true; // Если не можем определить, считаем устаревшим
  }
}

/**
 * Сохраняет массив taskId для анализа
 */
export function storeAnalysisTaskIds(analysisNumber: number, taskIds: string[]): void {
  try {
    const userId = getUserId();
    const key = getAnalysisKey(userId, analysisNumber);
    const existingData = getAnalysisData(analysisNumber);
    
    if (existingData) {
      // Обновляем существующие данные
      const updatedData: StoredAnalysisData = {
        ...existingData,
        task_ids: taskIds
      };
      localStorage.setItem(key, JSON.stringify(updatedData));
    } else {
      // Создаем новые данные (хотя это не должно происходить)
      console.warn(`Попытка сохранить taskId для несуществующего анализа ${analysisNumber}`);
    }
  } catch (error) {
    console.error('Ошибка при сохранении taskId анализа:', error);
  }
}

/**
 * Получает массив taskId для анализа
 */
export function getAnalysisTaskIds(analysisNumber: number): string[] {
  try {
    const storedData = getAnalysisData(analysisNumber);
    return storedData?.task_ids || [];
  } catch (error) {
    console.error('Ошибка при получении taskId анализа:', error);
    return [];
  }
}
